import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Scale } from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';

/* =====================================================================
   Baldas · TC-19 (alta y ajuste por pantalla) y TC-20 (el ámbar se ve)
   =====================================================================
   Una balda es una celda de carga bajo un contenedor, atada a un
   producto; hereda la repisa de su terminal. Lo que la báscula vio y
   nadie explicó queda en ámbar hasta que alguien lo reconozca.
   ===================================================================== */
export default function Baldas() {
  const avisos = useAvisos();
  const [tick, setTick] = useState(0);
  const recargar = useCallback(() => setTick((t) => t + 1), []);
  const [baldas, setBaldas] = useState(null);
  const [ambar, setAmbar] = useState([]);
  const [terminales, setTerminales] = useState([]);
  const [error, setError] = useState('');
  const [alta, setAlta] = useState(false);

  useEffect(() => {
    let vivo = true;
    Promise.all([api.get('/baldas'), api.get('/baldas/ambar'), api.get('/dispositivos').catch(() => [])])
      .then(([b, a, d]) => { if (vivo) { setBaldas(b); setAmbar(a); setTerminales((Array.isArray(d) ? d : []).filter((x) => x.tipo === 'TERMINAL_CONTEO')); setError(''); } })
      .catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  async function accion(fn, exito) {
    try { const r = await fn(); avisos.exito((r && r.mensaje) || exito || 'Hecho.'); recargar(); }
    catch (e) { avisos.error(e.message); }
  }

  if (error && !baldas) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!baldas) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo las baldas…
      </p>
    );
  }
  return (
    <div className="flex max-w-5xl flex-col gap-6 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Repisas con luz</h1>
        <p className="text-gray-600">Las repisas con báscula, qué producto lleva cada una, y lo que quedó en ámbar.</p>
        <Ayuda id="terminal.baldas.pantalla" />
      </header>

      <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <h2 className="font-semibold text-gray-900">En ámbar · {ambar.length}</h2>
        <Ayuda id="terminal.baldas.ambar" />
        {ambar.length === 0 ? <p className="text-sm text-gray-700">Nada pendiente: todo lo que vieron las básculas ya movió inventario.</p> : (
          <ul className="mt-2 divide-y divide-amber-100 text-sm">{ambar.map((l) => (
            <li key={l.id} className="flex flex-wrap items-center gap-x-3 py-1">
              <span className="text-gray-500">{String(l.ocurrido_en).slice(0, 16).replace('T', ' ')}</span>
              <span className="font-mono">{l.ubicacion || '—'}</span>
              <span className="font-medium">{l.producto}</span>
              <span className="tabular-nums">{l.piezas_antes ?? '?'} → {l.piezas} ({l.delta_piezas > 0 ? '+' : ''}{l.delta_piezas})</span>
              <button type="button" onClick={() => accion(() => api.post(`/baldas/lecturas/${l.id}/reconocer`, {}), 'Lectura reconocida: ya es un movimiento con tu nombre.')}
                      className="ml-auto rounded-xl bg-[var(--color-marca)] px-3 py-1 text-xs font-semibold text-white">Reconocer</button>
            </li>))}</ul>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Baldas</h2>
          <button type="button" onClick={() => setAlta((v) => !v)} className="inline-flex items-center gap-1 rounded-xl bg-[var(--color-marca)] px-3 py-1.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" aria-hidden="true" /> Nueva balda</button>
        </div>
        {alta && <Alta terminales={terminales} onHecho={(m) => { avisos.exito(m); setAlta(false); recargar(); }} />}
        {baldas.length === 0 ? (
          <NotaExplicativa titulo="Todavía no hay baldas">Una balda es una celda de carga bajo un contenedor. Se da de alta con la terminal que la lee, el canal y el producto; se calibra desde la terminal antes de encenderla.</NotaExplicativa>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 text-sm">{baldas.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-x-3 py-2">
              <Scale className="h-4 w-4 text-gray-400" aria-hidden="true" />
              <span className="font-mono">{b.ubicacion || '—'}</span>
              <span className="text-gray-600">{b.aparato} · canal {b.canal}</span>
              <span className="font-medium">{b.producto} ({b.sku})</span>
              <span className={`rounded px-2 text-xs ${b.activa ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{b.activa ? 'encendida' : 'apagada'}</span>
              <span className={`text-xs ${b.calibrada ? 'text-gray-500' : 'text-amber-700'}`}>{b.calibrada ? `${b.peso_unitario_mg} mg/pieza` : 'sin calibrar'}</span>
              <span className="text-xs text-gray-500">salta cada {b.salto_minimo_piezas}</span>
              <button type="button" disabled={!b.activa && !b.calibrada}
                      onClick={() => accion(() => api.patch(`/baldas/${b.id}`, { activa: !b.activa }))}
                      className="ml-auto text-xs font-semibold text-[var(--color-marca)] disabled:text-gray-400">{b.activa ? 'Apagar' : 'Encender'}</button>
            </li>))}</ul>
        )}
        <p className="mt-2 text-xs text-gray-500">Una balda sin calibrar no se puede encender: la calibración se hace en la terminal con una pieza de peso conocido.</p>
      </section>
    </div>
  );
}

function Alta({ terminales, onHecho }) {
  const [f, setF] = useState({ id_dispositivo: terminales[0] ? String(terminales[0].id) : '', sku: '', canal: '1', salto_minimo_piezas: '1' });
  const [error, setError] = useState('');
  const listo = f.id_dispositivo && f.sku.trim() && Number(f.canal) >= 1;
  return (
    <form onSubmit={async (e) => { e.preventDefault(); try { const r = await api.post('/baldas', { id_dispositivo: Number(f.id_dispositivo), sku: f.sku.trim().toUpperCase(), canal: Number(f.canal), salto_minimo_piezas: Number(f.salto_minimo_piezas) || 1 }); onHecho(`Balda dada de alta en ${r.ubicacion || 'la repisa de la terminal'}: ${r.producto}. Calíbrala desde la terminal para encenderla.`); } catch (err) { setError(err.message); } }}
          className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-gray-50 p-3">
      <label className="flex flex-col text-sm">Terminal *<select value={f.id_dispositivo} onChange={(e) => setF({ ...f, id_dispositivo: e.target.value })} className="rounded-lg border border-gray-300 p-2">{terminales.map((t) => <option key={t.id} value={t.id}>{t.nombre}{t.ubicacion ? ` · ${t.ubicacion}` : ''}</option>)}</select></label>
      <label className="flex flex-col text-sm">Producto (SKU) *<input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} className="rounded-lg border border-gray-300 p-2" /></label>
      <label className="flex flex-col text-sm">Canal<input type="number" min="1" max="32" value={f.canal} onChange={(e) => setF({ ...f, canal: e.target.value })} className="w-20 rounded-lg border border-gray-300 p-2" /></label>
      <label className="flex flex-col text-sm">Salto mínimo (piezas)<input type="number" min="1" value={f.salto_minimo_piezas} onChange={(e) => setF({ ...f, salto_minimo_piezas: e.target.value })} className="w-28 rounded-lg border border-gray-300 p-2" /></label>
      <button type="submit" disabled={!listo} className="rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300">Dar de alta</button>
      {error && <span className="text-sm text-red-700">{error}</span>}
      {terminales.length === 0 && <span className="text-xs text-amber-700">No hay terminales de conteo dadas de alta: primero conecta una en «Terminales de conteo».</span>}
    </form>
  );
}
